import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Container,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Alert,
  Chip,
  Stack,
} from '@mui/material';
import {
  ViewKanban as BoardIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useDataProvider, useNotify } from 'react-admin';
import { PageTitle } from '../../components/PageTitle';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import { ChromePicker } from 'react-color';

interface TaskStatus {
  id: string;
  name: string;
  color: string;
  order: number;
  isDefault?: boolean;
  canDelete?: boolean;
}

const DEFAULT_STATUSES: TaskStatus[] = [
  { id: 'Todo', name: 'To Do', color: '#6B7280', order: 0, isDefault: true, canDelete: false },
  { id: 'InProgress', name: 'In Progress', color: '#3B82F6', order: 1, isDefault: true, canDelete: false },
  { id: 'InReview', name: 'In Review', color: '#F59E0B', order: 2, isDefault: true, canDelete: true },
  { id: 'Done', name: 'Done', color: '#10B981', order: 3, isDefault: true, canDelete: false },
  { id: 'Blocked', name: 'Blocked', color: '#EF4444', order: 4, isDefault: true, canDelete: true },
  { id: 'Cancelled', name: 'Cancelled', color: '#9E9E9E', order: 5, isDefault: true, canDelete: true },
];

export const TaskStatusesConfig = () => {
  const navigate = useNavigate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  
  const [statuses, setStatuses] = useState<TaskStatus[]>(DEFAULT_STATUSES);
  const [editDialog, setEditDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<TaskStatus | null>(null);
  const [formData, setFormData] = useState({ name: '', color: '#1976d2' });
  const [showColorPicker, setShowColorPicker] = useState(false);

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const items = Array.from(statuses);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update order values
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index,
    }));

    setStatuses(updatedItems);
  };

  const handleAdd = () => {
    setCurrentStatus(null);
    setFormData({ name: '', color: '#1976d2' });
    setEditDialog(true);
  };

  const handleEdit = (status: TaskStatus) => {
    setCurrentStatus(status);
    setFormData({ name: status.name, color: status.color });
    setEditDialog(true);
  };

  const handleDelete = (status: TaskStatus) => {
    if (!status.canDelete) {
      notify('This status cannot be deleted', { type: 'warning' });
      return;
    }
    setCurrentStatus(status);
    setDeleteDialog(true);
  };

  const handleSaveStatus = () => {
    if (!formData.name.trim()) {
      notify('Status name is required', { type: 'warning' });
      return;
    }

    if (currentStatus) {
      // Edit existing
      setStatuses(prev => prev.map(s => 
        s.id === currentStatus.id 
          ? { ...s, name: formData.name, color: formData.color }
          : s
      ));
      notify('Status updated successfully', { type: 'success' });
    } else {
      // Add new
      const newStatus: TaskStatus = {
        id: formData.name.replace(/\s+/g, ''),
        name: formData.name,
        color: formData.color,
        order: statuses.length,
        isDefault: false,
        canDelete: true,
      };
      setStatuses(prev => [...prev, newStatus]);
      notify('Status added successfully', { type: 'success' });
    }

    setEditDialog(false);
    setShowColorPicker(false);
  };

  const handleConfirmDelete = () => {
    if (currentStatus) {
      setStatuses(prev => prev.filter(s => s.id !== currentStatus.id));
      notify('Status deleted successfully', { type: 'success' });
    }
    setDeleteDialog(false);
  };

  const handleSaveAll = async () => {
    try {
      // Save to backend - this would be tenant-specific configuration
      await dataProvider.update('crm/tenant-settings', {
        id: 'current', // Use current tenant
        data: { taskStatuses: statuses },
        previousData: {},
      });
      notify('Task statuses saved successfully', { type: 'success' });
    } catch (error) {
      notify('Failed to save task statuses', { type: 'error' });
      console.error('Failed to save:', error);
    }
  };

  return (
    <Box>
      <PageTitle
        title="Task Board Lanes"
        subtitle="Configure task statuses and workflow lanes"
        icon={<BoardIcon />}
      />
      
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/configuration')}
            sx={{ mb: 2 }}
          >
            Back to Configuration
          </Button>
        </Box>

        <Paper sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">
              Task Statuses
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Add Status
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 3 }}>
            Drag and drop to reorder statuses. The order here will determine the column order in the board view.
          </Alert>

          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="statuses">
              {(provided) => (
                <List {...provided.droppableProps} ref={provided.innerRef}>
                  {statuses.map((status, index) => (
                    <Draggable key={status.id} draggableId={status.id} index={index}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            mb: 1,
                            bgcolor: snapshot.isDragging ? 'action.hover' : 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                          }}
                        >
                          <Box {...provided.dragHandleProps} sx={{ mr: 2 }}>
                            <DragIcon color="action" />
                          </Box>
                          
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: 1,
                              bgcolor: status.color,
                              mr: 2,
                            }}
                          />
                          
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography>{status.name}</Typography>
                                {status.isDefault && (
                                  <Chip label="Default" size="small" variant="outlined" />
                                )}
                              </Stack>
                            }
                            secondary={`ID: ${status.id}`}
                          />
                          
                          <ListItemSecondaryAction>
                            <IconButton onClick={() => handleEdit(status)} size="small">
                              <EditIcon />
                            </IconButton>
                            {status.canDelete !== false && (
                              <IconButton onClick={() => handleDelete(status)} size="small">
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
            >
              Save Configuration
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Edit/Add Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {currentStatus ? 'Edit Status' : 'Add New Status'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <TextField
              label="Status Name"
              fullWidth
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              disabled={currentStatus?.isDefault}
              helperText={currentStatus?.isDefault ? 'Default status names cannot be changed' : ''}
            />
            
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Status Color
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 40,
                    borderRadius: 1,
                    bgcolor: formData.color,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowColorPicker(!showColorPicker)}
                />
                <TextField
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  size="small"
                  sx={{ width: 120 }}
                />
              </Box>
              {showColorPicker && (
                <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
                  <Box
                    sx={{ position: 'fixed', top: 0, right: 0, bottom: 0, left: 0 }}
                    onClick={() => setShowColorPicker(false)}
                  />
                  <ChromePicker
                    color={formData.color}
                    onChange={(color) => setFormData({ ...formData, color: color.hex })}
                  />
                </Box>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialog(false);
            setShowColorPicker(false);
          }}>
            Cancel
          </Button>
          <Button onClick={handleSaveStatus} variant="contained">
            {currentStatus ? 'Save' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the status "{currentStatus?.name}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Any tasks with this status will need to be updated manually.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};